import {useTranslation} from "react-i18next";
import {css, Theme, useTheme} from "@emotion/react";
import {Button} from "@pg-design/button";
import {Col, Container, Row} from "@pg-design/grid";
import {
    backgroundColor,
    borderRadius,
    flex,
    flexAlignCenter,
    flexDirection,
    mb,
    onDesktop,
    p,
    pb,
    ph,
    pv,
    w100
} from "@pg-design/helpers-css";
import {BrandEmailIcon, CheckIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

import {LANDING_WIDE_COL_PROPS} from "../features/landing/components/landingMarketNews.constants";
import {flexGap, flexShrink} from "./css_helpers/flex";

/** CTA prowadzi do rejestracji w BIG DATA Insight; `ref` zostaje dla atrybucji po ich stronie. */
const INSIGHT_SIGNUP_URL = "https://insight.rynekpierwotny.pl/?ref=bigdata.rynekpierwotny.pl#/portal/signup";

const BENEFIT_KEYS = [
    "landing.newsletter.benefit_1",
    "landing.newsletter.benefit_2",
    "landing.newsletter.benefit_3",
    "landing.newsletter.benefit_4"
] as const;

interface INewsletterProps {
    variant?: "landing";
    id?: string;
}

export const Newsletter = ({variant, id}: INewsletterProps = {}) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const cardContent = (
        <div css={card(theme)}>
            <div css={iconColumn}>
                <BrandEmailIcon size="19" wrapperSize="19" wrapperColor={theme.colors.primary} css={mainIcon} />
            </div>

            <div css={contentColumn}>
                <Text as="h2" variant="headline_2" css={title}>
                    {t("landing.newsletter.title")}
                </Text>

                <div css={benefitsRow}>
                    <ul css={benefitsList}>
                        {BENEFIT_KEYS.map((key) => (
                            <li css={benefitItem} key={key}>
                                <CheckIcon
                                    size="1.6"
                                    css={checkIcon}
                                    wrapperColor={theme.colors.primary}
                                    wrapperSize="1.6"
                                />
                                <Text variant="body_copy_1">{t(key)}</Text>
                            </li>
                        ))}
                    </ul>

                    <Button
                        css={submitButton}
                        size="medium"
                        variant="filled_primary"
                        href={INSIGHT_SIGNUP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t("landing.newsletter.submit")}
                    </Button>
                </div>
            </div>
        </div>
    );

    if (variant === "landing") {
        return (
            <Container as="section" id={id} css={[sectionWrapper, landingSectionWrapper]}>
                <Row>
                    <Col {...LANDING_WIDE_COL_PROPS}>{cardContent}</Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid id={id} css={[sectionWrapper, defaultSectionWrapper]} as="section">
            <Container>{cardContent}</Container>
        </Container>
    );
};

const sectionWrapper = css`
    ${w100};
`;

const defaultSectionWrapper = css`
    ${pv(7)};

    ${onDesktop(css`
        ${pv(10)};
    `)};
`;

const landingSectionWrapper = css`
    ${ph(1)};
    ${pb(4)};
`;

const card = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    ${flexAlignCenter};
    ${flexGap(3)};
    ${p(3)};
    ${borderRadius(2)};
    ${backgroundColor(theme.colors.background[200])};

    ${onDesktop(css`
        ${flexDirection("row")};
        ${flexAlignCenter};
        ${p(4, 5)};
        ${flexGap(4)};
    `)};
`;

const iconColumn = css`
    ${flexShrink(0)};
`;

const mainIcon = css`
    ${flexShrink(0)};
`;

const checkIcon = css`
    ${flexShrink(0)};
`;

const contentColumn = css`
    flex: 1 1 auto;
    min-width: 0;
    ${w100};
`;

/** Pełna szerokość – wcześniej stał w flex-rowie obok CTA i swoim `max-content` rozpychał kartę. */
const title = css`
    ${mb(3)};
`;

const benefitsRow = css`
    ${flex()};
    ${flexDirection("column")};
    ${flexGap(3)};

    ${onDesktop(css`
        ${flexDirection("row")};
        ${flex("center", "flex-start")};
        ${flexGap(4)};
    `)};
`;

const benefitsList = css`
    list-style: none;
    margin: 0;
    padding: 0;
    min-width: 0;
`;

const benefitItem = css`
    ${flexAlignCenter};
    ${flexGap(1.5)};
    ${mb(1.5)};

    &:last-of-type {
        margin-bottom: 0;
    }
`;

const submitButton = css`
    && {
        ${p(2)};
        ${flexShrink(0)};
        text-transform: uppercase;
        white-space: normal;
    }
`;
